import { Button, Menu, MenuItem, CircularProgress } from "@mui/material";
import { alpha } from '@mui/material/styles';
import { useDispatch } from "react-redux";
import { useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import { useSelector } from "../../../../../../redux/store";
import { editTicketData, updateSingleTicket } from "../../../../../../redux/slices/tickets";
import { priorityColor } from '../../../../../../utils/cssStyles';
import { postSetTicketMessagesRead } from "../../../../../../api/tickets";
import { getUnreadData } from "../../../../../../redux/slices/dashboard";
import { AwaitButton } from "../../../../../../reusable-components/await-button";
import Iconify from "../../../../../../reusable-components/iconify";
import TicketAutoCompleteActions from "../../TicketAutoCompleteActions";
import { markMetricRemoved, markMetricAdded, NotificationMetricType } from "../../../../../../redux/slices/notifications";

export default function TicketActions() {
  const { id = '' } = useParams();
  const { mainTicket } = useSelector((state) => state.tickets.data.ticketInfoById[id] || {});
  const ticketPriorityLevels = ['Low', 'Medium', 'High', 'Maximum'];
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState(null);
  const [isTicketRead, setIsTicketRead] = useState(true);
  const [isPriorityLoading, setIsPriorityLoading] = useState(false);

  const dispatch = useDispatch();

  const handleChangePriority = (priority) => {
    setIsPriorityLoading(true);
    setPriorityMenuAnchor(null);
    dispatch(editTicketData({ Id: id, value: priority, updateType: 'Importance' }))
      .then(() => {
        enqueueSnackbar(`Ticket marked as ${priority} priority`, { variant: 'success' });
      })
      .catch(() => enqueueSnackbar(`Failed to mark ticket as ${priority} priority`, { variant: 'error' }))
      .finally(() => setIsPriorityLoading(false));
  };

  const handleOpenPriorityMenu = (event) => {
    setPriorityMenuAnchor(event.currentTarget);
  };

  const handleClosePriorityMenu = () => {
    setPriorityMenuAnchor(null);
  };

  const handleMarkAsUnread = async (markAsUnread) => {
    try {
      const response = await postSetTicketMessagesRead({ id, markAsUnread });
      if (response?.status === 200) {
        dispatch(
          updateSingleTicket({
            id,
            messageCount: markAsUnread ? 1 : 0,
          })
        );
        setIsTicketRead(!markAsUnread);
        dispatch(getUnreadData());
        if (markAsUnread) {
          dispatch(markMetricAdded(NotificationMetricType.TicketsUnread, id));
        } else {
          dispatch(markMetricRemoved(NotificationMetricType.TicketsUnread, id));
        }
      }
    } catch (error) {
      enqueueSnackbar(`Error: Could not mark ticket as unread`, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <TicketAutoCompleteActions id={id} spacing={0.5} navigateAfterAction={false} />
      <AwaitButton
        variant={isTicketRead ? "outlined" : "contained"}
        color="secondary"
        sx={{ my: 0.5, boxShadow: 'none' }}
        onClick={() => handleMarkAsUnread(isTicketRead)}
      >
        Mark as {isTicketRead ? `Unread` : `Read`}
      </AwaitButton>

      <Button
        variant="outlined"
        sx={{
          my: 0.5,
          boxShadow: 'none',
          fontSize: '14px',
          textAlign: 'center',
          border: (theme) => `solid 1px ${alpha(priorityColor(mainTicket.importance, theme), 0.5)}`,
          color: (theme) => priorityColor(mainTicket.importance, theme),
          '&:hover': {
            backgroundColor: (theme) => alpha(priorityColor(mainTicket.importance, theme), 0.03),
            borderColor: (theme) => priorityColor(mainTicket.importance, theme),
          },
        }}
        onClick={handleOpenPriorityMenu}
        endIcon={!isPriorityLoading && <Iconify icon="mdi:chevron-down" />}
        disabled={isPriorityLoading}
      >
        {isPriorityLoading ?
          <CircularProgress size={20} /> :
          mainTicket.importance && !isPriorityLoading? `${mainTicket.importance} Priority` : 'Set Priority'}
      </Button>
      <Menu
        anchorEl={priorityMenuAnchor}
        open={Boolean(priorityMenuAnchor)}
        onClose={handleClosePriorityMenu}
        PaperProps={{
          style: {
            width: priorityMenuAnchor ? priorityMenuAnchor.offsetWidth : undefined,
          },
        }}
      >
        {ticketPriorityLevels.map(level => (
          <MenuItem
            key={level}
            selected={mainTicket.importance === level}
            onClick={() => handleChangePriority(level)}
          >
            {level} Priority
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}